// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { MedplumClient, WithId } from '@medplum/core';
import type { Patient } from '@medplum/fhirtypes';
import { assertMrnUnchanged } from '@polarissleep/shared';
import { getPolarisEnv } from './polaris-env-context.js';

/**
 * Every Patient update in the provider-app must go through this module —
 * direct `medplum.updateResource(patient)` for a Patient is a bug.
 *
 * The wrapper reads the stored Patient, asserts MRN invariants
 * (immutability, env-appropriate format, no removal), then issues the update.
 * Errors surface at the caller's stack with a clear message instead of from a
 * Subscription bot four layers down.
 *
 * TODO: enforce this convention with a Biome lint rule (or CI grep) so a
 * direct `medplum.updateResource<Patient>` outside this file fails review.
 * Until then, search-and-replace plus code review.
 */
export async function updatePatientSafely(medplum: MedplumClient, incoming: Patient): Promise<WithId<Patient>> {
  if (!incoming.id) {
    // No id: this is a create, not an update. Defer to the SDK; MRN
    // generation belongs in createPatientWithMrn, not here.
    return medplum.updateResource(incoming);
  }
  const stored = await medplum.readResource('Patient', incoming.id);
  assertMrnUnchanged(stored, incoming, getPolarisEnv());
  return medplum.updateResource(incoming);
}
